import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { CSSProperties, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { Employees } from '~/data/employees/types';
import { useAllOrgUnits } from '~/data/orgunits';
import { getProfilePictureUrl } from '~/utils/helper';

import { useSxStyles } from './useStyles';

type PropsType = {
  employee: Employees;
  showNew?: boolean;
  handleClick: (id: number, unitId: number) => void;
  showEmpNumber?: boolean;
  style?: CSSProperties;
};

export const SearchedItem = (props: PropsType) => {
  const { employee, showNew, handleClick, showEmpNumber, style } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { orgUnitsIncludeDeleted } = useAllOrgUnits();
  const employeePositionAndUnit = useMemo(() => {
    const foundUnit = orgUnitsIncludeDeleted.find(
      (org) => org.fOrgUnitId === employee.fEmpUnit,
    );

    return !!foundUnit
      ? `${employee.fEmpPosition}, ${foundUnit.fOrgUnitName} (${foundUnit.fOrgUnitNumber})`
      : employee.fEmpPosition;
  }, [orgUnitsIncludeDeleted, employee]);

  const onClick = useCallback(() => {
    handleClick(employee.fEmpId, employee.fEmpUnit);
  }, [employee, handleClick]);

  return (
    <ListItem
      disableGutters={true}
      sx={sxStyles.listItemRoot}
      style={style}
      component={'div'}
    >
      <ListItemButton onClick={onClick} sx={sxStyles.listItemButton}>
        <ListItemAvatar sx={sxStyles.listItemAvatar}>
          <Avatar src={getProfilePictureUrl(employee.fEmpProfPictureId)} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <>
              <Typography
                variant="body1"
                noWrap={true}
                lineHeight={1}
                sx={sxStyles.primaryElement}
              >
                {`${employee.fEmpLastName}, ${employee.fEmpFirstName}`}
              </Typography>
              {showNew && employee.fEmpStatus === 'NEW' && (
                <Typography
                  variant="body2"
                  sx={{ ...sxStyles.primaryElement, ...sxStyles.statusNew }}
                >
                  {t('laNew')}
                </Typography>
              )}
            </>
          }
          secondary={
            <>
              <Box>
                <TooltippedTruncatedText>
                  {employeePositionAndUnit}
                </TooltippedTruncatedText>
              </Box>
              {showEmpNumber ? <Box>{employee.fEmpPersonNumber}</Box> : null}
            </>
          }
          slotProps={{
            secondary: {
              component: 'div',
              sx: sxStyles.secondaryElement,
            },
          }}
          sx={{ margin: 0 }}
        />
      </ListItemButton>
    </ListItem>
  );
};
